/**
 * The editor's controls, lifted into the application header.
 *
 * ⚠️ THE EDITOR USED TO CARRY TWO FULL-WIDTH BARS OF ITS OWN, under the header, and on a 360 px phone
 * those three stacked rows took a third of the screen before a single pixel of the customer's site
 * appeared. They are one dropdown now — but the header lives ABOVE `<Routes>` and the state those
 * controls act on lives inside the editor, so something has to cross that boundary.
 *
 * ⚠️ A CONTEXT WHOSE VALUE THE EDITOR *REGISTERS*, RATHER THAN STATE HOISTED INTO `App`. Moving
 * `pane`, `full`, `previewLang` and the undo stack up to the shell would make every keystroke in the
 * editor re-render the marketing page's provider tree, and would put editor concepts in a component
 * that also renders `/report` for strangers. This way the editor keeps its own state and merely
 * publishes a handle to it while it is mounted; every other route sees `null` and renders nothing.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * ⚠️ THERE USED TO BE A THIRD PANE, `inspector`, AND IT IS GONE. Editing a block was a whole screen
 * you navigated TO: the preview disappeared, you changed a heading, and you came back to find out
 * what it looked like. A builder whose defining feature is showing you the page cannot take the page
 * away at the moment you change it. The fields are a drawer over the preview now — see `BlockDrawer`.
 */
export type Pane = 'rail' | 'stage'

/**
 * ⚠️ FIVE STATES, AND THE FIFTH IS NOT A SYNONYM FOR THE FIRST. `local` means the document has been
 * written down in this tab and nowhere else, which is what one of the landing page's examples is
 * until somebody keeps it. Folding it into `clean` would have this menu say *Saved* about work that
 * dies with the tab — §0.4 says the claim belongs to whatever knows, and this is the thing that knows.
 */
export type SaveState = 'clean' | 'dirty' | 'saving' | 'failed' | 'local'

export type EditorChrome = {
  pane: Pane
  setPane: (pane: Pane) => void
  full: boolean
  setFull: (full: boolean) => void
  undo: () => void
  canUndo: boolean
  saving: SaveState
  /**
   * The site's colour scheme, up here because the control that changes it is up here.
   *
   * ⚠️ THE HEADER HOLDS THE TRIGGER AND THE DRAWER HOLDS THE CHOICES, WHICH IS THE WHOLE DESIGN. A
   * palette in a dropdown covers the page it is repainting, so every choice would be judged by the
   * two centimetres of site visible around the menu. The drawer takes half the screen at most and the
   * other half is the customer's own page changing colour as they tap — which is the only way to
   * choose a colour scheme.
   */
  template: string
  setTemplate: (id: string) => void
  /** Whether the drawer is showing the colour schemes. */
  theme: boolean
  setTheme: (open: boolean) => void
  /**
   * Present only while the site on screen belongs to nobody — one of the examples, or a site the
   * model wrote for somebody with no account.
   *
   * ⚠️ THIS BUTTON IS THE HONESTY, NOT JUST THE ACTION, AND THAT IS WHY IT IS IN THE HEADER RATHER
   * THAN ONLY IN A DIALOG. The arrival dialog can be dismissed — that is the point of it — and a
   * claim that can be dismissed cannot be the only place the product says "this is not saved
   * anywhere". A permanently visible *Keep this site* says the same thing without a banner: its
   * presence means it has not been kept, and it disappears the moment it has. §0.4 asks for the
   * claim to be rendered by whatever knows; this is that, in the smallest form that survives a
   * dismissal.
   */
  keep?: { busy: boolean; onKeep: () => void }
  /**
   * Whether this site can be published — true for a real one, absent for a demonstration.
   *
   * ⚠️ IT IS A FLAG RATHER THAN A HANDLER, BECAUSE THE ACT IS NAVIGATION AND THE HEADER OWNS THE
   * PANES. Going to the publish panel is `setPane('rail')` plus a scroll, both of which are the
   * header's own business; what the header cannot know is whether there is anything to publish, and
   * that is the one bit the editor has to lend it.
   *
   * ⚠️ IT SHARES A SLOT WITH `keep`, AND THEY ARE MUTUALLY EXCLUSIVE BY CONSTRUCTION. A site that
   * lives in the browser has no id and cannot be published; a site with an id has already been kept.
   * The header therefore carries exactly one call to action — the next thing this site needs — and
   * never two.
   */
  publishable?: boolean
  /**
   * Open the publish wizard.
   *
   * > ⚠️ **THIS WAS A FLAG AND A SCROLL — `setPane('rail')` PLUS `scrollIntoView('#ed-publish')`.**
   * > The reasoning was that going to the panel is navigation and the header owns the panes, which
   * > was right while the panel WAS a pane. It is a dialog now, so the header no longer needs to know
   * > where publishing lives — only that this site has one.
   */
  openPublish?: () => void
  /**
   * The address a visitor would land on, present only while the published snapshot IS what is in the
   * editor.
   *
   * ⚠️ IT IS ABSENT THE MOMENT ANYTHING CHANGES, AND THAT IS THE WHOLE POINT OF IT. Publishing writes
   * an immutable snapshot (§2.2), so a customer who fixes a phone number is looking at a document
   * that no visitor can see — and a header still offering «Open» would be pointing them at the old
   * one while calling it their site. `publishable` and this are the two halves of one question: what
   * does this site need next, another publish or a look at what is already there.
   */
  liveUrl?: string
}

const Ctx = createContext<{ chrome: EditorChrome | null; publish: (chrome: EditorChrome | null) => void }>({
  chrome: null,
  publish: () => {},
})

export function EditorChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, publish] = useState<EditorChrome | null>(null)
  const value = useMemo(() => ({ chrome, publish }), [chrome])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** What the header reads. `null` everywhere except inside the editor. */
export function useEditorChrome(): EditorChrome | null {
  return useContext(Ctx).chrome
}

/**
 * Called by the editor to lend its controls to the header.
 *
 * ⚠️ IT CLEARS ON UNMOUNT, which is the whole reason it is an effect. Leaving a stale handle behind
 * would give the sites list a dropdown that moves panes in an editor nobody is looking at.
 */
export function usePublishEditorChrome(chrome: EditorChrome): void {
  const { publish } = useContext(Ctx)
  useEffect(() => {
    publish(chrome)
    return () => publish(null)
  }, [publish, chrome])
}
